// =============================================================================
// src/views/demografica/demografica-view.ts
//
// AppModule da Tela 04 · Analise Demografica. render() emite a marcacao fiel e
// init(ctx) liga os listeners (chips multi-select, limpar, remover tag, drawer
// "editar recorte") + sincronizacao inicial.
//
// Proveniencia: assets/unified-app.js Section 4.4 (Sections.demografica),
//   linhas 1281-1388. A funcao activate(mapCtrl) original vira init(ctx), lendo
//   o MapController de ctx.map (DI). A logica de _syncRecorte/_applyStyles foi
//   extraida para demografica-sync.ts (SRP).
//
// Regra Clean Code: funcoes 4-20 linhas, early-return, sem `this`/singleton.
// =============================================================================

import type { AppContext, AppModule, MapController } from '../../app/types';
import { renderDemograficaSection } from './demografica-template';
import {
  createDemograficaRecorteState,
  syncDemograficaRecorte,
  type DemograficaRecorteState,
} from './demografica-sync';

/** Id do modulo no contrato (AppModule.id), greppavel. */
const DEMOGRAFICA_MODULE_ID = 'demografica' as const;

/**
 * Liga um listener UMA VEZ por elemento, via flag no proprio no — equivalente
 * fiel a UI.bindOnce (assets/unified-app.js linhas 825-831). Re-init nao
 * duplica handlers, preservando o comportamento idempotente do original.
 */
function bindListenerOnce(
  element: Element | null,
  eventName: string,
  bindKey: string,
  listener: EventListener,
): void {
  if (!element) return;
  const flag = '__bound_' + eventName + '_' + bindKey;
  const flagged = element as unknown as Record<string, boolean>;
  if (flagged[flag]) return;
  flagged[flag] = true;
  element.addEventListener(eventName, listener);
}

/** Liga o toggle .selected de cada chip de recorte + re-sync. */
function bindRecorteChips(
  section: HTMLElement,
  state: DemograficaRecorteState,
  mapCtrl: MapController,
): void {
  section.querySelectorAll('.u-filter-group .u-chip[data-g]').forEach((chip) => {
    bindListenerOnce(chip, 'click', 'demografica-chip', () => {
      chip.classList.toggle('selected');
      syncDemograficaRecorte(state, mapCtrl);
    });
  });
}

/** Liga o botao "limpar": remove .selected de todos os chips + re-sync. */
function bindClearButton(
  section: HTMLElement,
  state: DemograficaRecorteState,
  mapCtrl: MapController,
): void {
  const clear = document.getElementById('u-demo-clear');
  bindListenerOnce(clear, 'click', 'demografica-clear', () => {
    section
      .querySelectorAll('.u-filter-group .u-chip.selected')
      .forEach((chip) => chip.classList.remove('selected'));
    syncDemograficaRecorte(state, mapCtrl);
  });
}

/** Remove (via delegacao) todos os chips de um grupo ao clicar no "×". */
function removeRecorteGroup(
  section: HTMLElement,
  clickEvent: Event,
  state: DemograficaRecorteState,
  mapCtrl: MapController,
): void {
  const eventTarget = clickEvent.target as HTMLElement;
  if (!eventTarget.classList.contains('rm')) return;
  const tag = eventTarget.closest('.u-recorte-chip') as HTMLElement | null;
  if (!tag) return;
  const group = tag.dataset.g;
  section
    .querySelectorAll('.u-filter-group .u-chip[data-g="' + group + '"]')
    .forEach((chip) => chip.classList.remove('selected'));
  syncDemograficaRecorte(state, mapCtrl);
}

/** Liga a delegacao de remocao de tag na faixa de tags ativas. */
function bindActiveTagsRemoval(
  section: HTMLElement,
  state: DemograficaRecorteState,
  mapCtrl: MapController,
): void {
  const activeTags = document.getElementById('u-demo-active-tags');
  bindListenerOnce(activeTags, 'click', 'demografica-remove-tag', (clickEvent) => {
    removeRecorteGroup(section, clickEvent, state, mapCtrl);
  });
}

/** Abre/fecha o drawer "editar recorte" alternando o atributo hidden. */
function toggleEditDrawer(editTrigger: HTMLElement, editDrawer: HTMLElement): void {
  const isOpening = editDrawer.hasAttribute('hidden');
  if (isOpening) {
    editDrawer.removeAttribute('hidden');
    editTrigger.setAttribute('aria-expanded', 'true');
    return;
  }
  editDrawer.setAttribute('hidden', '');
  editTrigger.setAttribute('aria-expanded', 'false');
}

/** Liga o trigger e o botao de fechar do drawer "editar recorte". */
function bindEditDrawer(): void {
  const editTrigger = document.getElementById('u-demo-edit-trigger');
  const editDrawer = document.getElementById('u-demo-edit-drawer');
  if (!editTrigger || !editDrawer) return;
  bindListenerOnce(editTrigger, 'click', 'demografica-edit-toggle', () => {
    toggleEditDrawer(editTrigger, editDrawer);
  });
  const editClose = editDrawer.querySelector('.u-demo-edit-close');
  bindListenerOnce(editClose, 'click', 'demografica-edit-close', () => {
    editDrawer.setAttribute('hidden', '');
    editTrigger.setAttribute('aria-expanded', 'false');
  });
}

/**
 * AppModule da secao demografica. render() = marcacao fiel; init() = wiring.
 *
 * Nota de fidelidade: o original tinha um `deactivate()` que zerava
 * MapController.currentZoneStyleFn (campo global nao exposto no contrato
 * MapController). Como AppModule nao define deactivate e o contrato nao expoe
 * esse campo, o reset foi omitido — ver resumo de desvios. Se o mapa ainda nao
 * montou (ctx.map === null), o wiring que precisa do MapController e adiado.
 */
export const demograficaView: AppModule = {
  id: DEMOGRAFICA_MODULE_ID,

  render(): string {
    return renderDemograficaSection();
  },

  init(ctx: AppContext): void {
    const section = document.getElementById('section-demografica');
    const mapCtrl = ctx.map;
    if (!section || !mapCtrl) return;
    const state = createDemograficaRecorteState();
    bindRecorteChips(section, state, mapCtrl);
    bindClearButton(section, state, mapCtrl);
    bindActiveTagsRemoval(section, state, mapCtrl);
    bindEditDrawer();
    syncDemograficaRecorte(state, mapCtrl);
  },
};
